import { DownloadButtonData } from "@nova/component-export-download-buttons-hover"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { config } from "@nova/configuration/browser"
import { GeometryType } from "@nova/constants"
import { createRulerKml, createRulerPolygonKml } from "@nova/data-create-shape-kml"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { selectRulerById } from "features/entity-caching/rulers/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { lineTransformer } from "integrations/geojson/transformers/line"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { useCallback } from "react"
import { download, downloadUri } from "tools/browser/download"
import { setGlobalNotificationContext } from "user/profile/slice"

export const useRulerDownloadButtonData = (): DownloadButtonData => {
    const dispatch = useAppDispatch()

    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(-1)
    const id = selectedLayerId || ""
    const fireId = useAppSelector(selectActivatedFireId) || ""
    const { name: projectName } = useAppSelector(selectFireById(fireId))!

    const ruler = useAppSelector(selectRulerById(id))
    const { organizationSettingCRS } = config.use("organizationSettingCRS")

    const getFilename = useCallback(
        (end: string) => `${projectName} ${ruler.name}${end}`,
        [projectName, ruler]
    )

    const downloadKml = useCallback(() => {
        if (!ruler) return
        const kmlName = projectName + " - " + ruler.name
        download(
            getFilename(".kml"),
            "application/vnd.google-earth.kml+xml",
            ruler.geometryType === GeometryType.Polygon
                ? createRulerPolygonKml([ruler], kmlName)
                : createRulerKml([ruler], kmlName)
        )
    }, [ruler, getFilename, projectName])

    const downloadShp = useCallback(async () => {
        const type = ruler.geometryType === GeometryType.Line ? "Lines" : "Polygons"
        const geojson = lineTransformer(
            [
                {
                    name: ruler.name,
                    points: ruler.coordinates.map(({ longitude, latitude }) => [
                        longitude,
                        latitude,
                    ]),
                },
            ],
            coordinateSystems[organizationSettingCRS].proj4
        )

        const res = await fetch(
            "/api/ogr2ogr?crs=" + coordinateSystems[organizationSettingCRS].code + "&type=" + type,
            {
                method: "POST",
                body: new File([new Blob([JSON.stringify(geojson)])], `${type}.geojson`),
            }
        )
        if (res.status >= 300) {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to create shapefile.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
        const blob = await res.blob()

        await downloadUri(getFilename(".zip"), URL.createObjectURL(blob))
    }, [
        organizationSettingCRS,
        dispatch,
        getFilename,
        ruler.coordinates,
        ruler.geometryType,
        ruler.name,
    ])

    return {
        exportOptions: {
            KML: {
                callback: downloadKml,
            },
            SHP: {
                callback: downloadShp,
            },
        },
    }
}
